import { Injectable } from '@angular/core';
import { merge, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { VBrickPlayerComponent, VideogularComponent } from '@vbrick/vbrick-player';
import { PushBus } from 'rev-shared/push/PushBus.Service';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { WebcastStatus } from 'rev-portal/scheduledEvents/webcast/WebcastStatus';

import { EmbedPlayerMessageBus } from './EmbedPlayerMessageBus.Service';
import { parentMessages$, publishParentMessage } from '../security/EmbedMessageBus';
import { ISDKMethodHandlers } from './ISDKContracts';
import { TSDKWebcastMethod, ISDKWebcastInfo, ISDKWebcastLayout, WebcastStatus as SDKWebcastStatus } from './ISDKWebcastContracts';
import { IComment } from '../webcast/webcastView/chat/Contract';

@Injectable({
	providedIn: 'root'
})
export class EmbedWebcastService {

	constructor(
		private readonly PushBus: PushBus,
		private readonly EmbedPlayerMessageBus: EmbedPlayerMessageBus
	)
	{}

	public start(webcast: WebcastModel): Observable<any> {

		publishParentMessage('webcastLoaded', this.getInfo(webcast));

		const webcastEvents = this.PushBus.getRxJsObservable(webcast.id, '', [
			'WebcastStarted', 'BroadcastStarted', 'BroadcastStopped', 'WebcastEnded'
		]).pipe(
			tap(m => {
				const type = m.type;
				const data: any = {
					status: m.data.webcastStatus || m.data.status
				};

				if(type === 'WebcastStarted') {
					const run = webcast.getRunItem(m.data.runNumber);
					data.isPreProduction = run?.isPreProduction;
				}

				const webcastStatus: any = type.substring(0, 1).toLowerCase() + type.substring(1);
				publishParentMessage(webcastStatus, data);
			})
		);

		return merge(
			webcastEvents,
			this.handleWebcastEvents(webcast),
			this.handleLayoutEvents(webcast)
		);
	}

	public handlePlayerEvents(player: VBrickPlayerComponent, vgApi: VideogularComponent): Observable<any> {
		return this.EmbedPlayerMessageBus.handlePlayerEvents(player, vgApi);
	}

	public handleLayoutEvents(webcast: WebcastModel): Observable<any> {
		const parentMessageHandlers: ISDKMethodHandlers<TSDKWebcastMethod> = {
			updateLayout: ({ msg }) => {
				if (msg.video !== undefined) {
					webcast.layout.isVideoVisible = !!msg.video;
				}
				if (msg.presentation !== undefined) {
					webcast.layout.isPresentationVisible = !!msg.presentation;
				}
				publishParentMessage('layoutChanged', this.getLayout(webcast));
			}
		};
		return parentMessages$.pipe(
			tap(e => {
				parentMessageHandlers[e.event]?.(e);
			})
		);
	}

	public handleWebcastEvents(webcast: WebcastModel): Observable<any> {
		return this.PushBus.getObservable(webcast.id, '', {
			WebcastCommentAdded: (comment: IComment) => {
				publishParentMessage(
					'commentAdded',
					{
						comment: comment.comment,
						date: comment.date,
						userId: comment.userId,
						firstname: comment.userFirstName,
						lastname: comment.userLastName
					});
			},
			CurrentPresentationSlideSet: msgData => {
				publishParentMessage(
					'slideChanged',
					{
						slideNumber: msgData.slideNumber,
						slideDelay: webcast.presentation?.slideDelayMilliseconds || 0
					});
			},
			PollOpened: msgData => {
				publishParentMessage(
					'pollOpened',
					{
						pollId: msgData.pollId,
						question: msgData.question,
						answers: (msgData.answers || []).map(text => ({ text })),
						multipleChoice: msgData.multipleChoice
					});
			},
			PollClosed: msgData => {
				publishParentMessage('pollClosed', { pollId: msgData.pollId });
			},
			PollPublished: msgData => {
				publishParentMessage(
					'pollPublished',
					{
						pollId: msgData.pollId,
						question: msgData.question,
						answers: msgData.answers,
						multipleChoice: msgData.multipleChoice,
						totalResponses: msgData.totalResponses
					});
			},
			PollUnpublished: msgData => {
				publishParentMessage('pollUnpublished', { pollId: msgData.pollId });
			},
			PresentationUploadStarted: () => {
				publishParentMessage('layoutChanged', this.getLayout(webcast));
			},
			WebcastPresentationRemoved: () => {
				publishParentMessage('layoutChanged', this.getLayout(webcast));
			}
			// WebcastDetailsSaved: msgData => {
			// 	// publishParentMessage('webcastLoaded', this.getWebcastInfo(webcast));
			// }
		});
	}

	private getInfo(webcast: WebcastModel): ISDKWebcastInfo {
		const subs = webcast.liveSubtitles;
		const liveLanguages: string[] = subs?.isLiveSubtitlesEnabled
			? [subs.sourceLanguage, ...(subs.translationLanguages || [])]
			: [];

		return {
			webcastId: webcast.id,
			status: this.getStatus(webcast.status),
			isPreProduction: webcast.currentRun?.isPreProduction,
			title: webcast.title,
			startDate: webcast.startDate?.toISOString(),
			endDate: webcast.endDate?.toISOString(),
			subtitles: liveLanguages.map(language => ({ language })),
			linkedVideoId: webcast.linkedVideoId,
			isLive: true
		};
	}

	private getLayout(webcast: WebcastModel): ISDKWebcastLayout {
		const payload: ISDKWebcastLayout = {
			video: webcast.layout.isVideoVisible
		};
		if (webcast.presentation.hasPresentation) {
			payload.presentation = webcast.layout.isPresentationVisible;
		}
		return payload;
	}

	private getStatus(status: WebcastStatus): SDKWebcastStatus {
		switch(status) {
			case WebcastStatus.Starting:
			case WebcastStatus.Deleted:
			case WebcastStatus.VideoSourceStarting:
			case WebcastStatus.WaitingForStream:
				return SDKWebcastStatus.Scheduled;

			case WebcastStatus.RecordingStarting:
			case WebcastStatus.RecordingStopping:
			case WebcastStatus.Recording:
				return SDKWebcastStatus.Broadcasting;
		}
		return status as any;
	}
}
